import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useKeycloak } from "@react-keycloak/web";
import { setUiVersion, UiVersion } from "../../uiVersion";
import { mapPathAcrossVersions } from "./versionPathMapping";
import { getRoute, getRoutesByIntegration } from "../../api/routes/routesApi";

// GUNDI-5639: the switch between the current portal and the frozen legacy one.
// New code (not a frozen import), mounted by BOTH trees with its own variant,
// so it may use the CURRENT api modules for the path translation.
//
// Not dismissible in this first version (product call): the switch is the
// whole point of the trial, so it stays on screen wherever it is shown.
type VersionBannerProps = { variant: UiVersion };

// Only detail pages of the two renamed entities get the banner: a route
// (legacy vocabulary: connection) or a connection (legacy vocabulary:
// destination). On the lists — and everywhere else — the two versions look
// close enough that the banner is noise. `new` is the wizard, not a detail.
const DETAIL_PATHS: Record<UiVersion, RegExp[]> = {
    new: [/^\/routes\/(?!new(\/|$))[^/]+/, /^\/connections\/(?!new(\/|$))[^/]+/],
    legacy: [/^\/connections\/(?!new(\/|$))[^/]+/, /^\/destinations?\/(?!new(\/|$))[^/]+/],
};

export const showsVersionBannerAt = (variant: UiVersion, pathname: string): boolean =>
    DETAIL_PATHS[variant].some((pattern) => pattern.test(pathname));

// The banner lives inside the FIXED nav wrapper, so its 28px are invisible to
// the page's static pt-[115px] offset. The padding wrappers add the 28px back
// on the pages that show it. Single source of truth, so the strip and the
// compensation can never disagree.
export function useVersionBannerVisible(variant: UiVersion): boolean {
    const { pathname } = useLocation();
    return showsVersionBannerAt(variant, pathname);
}

export default function VersionBanner({ variant }: VersionBannerProps) {
    const { t } = useTranslation();
    const { keycloak } = useKeycloak();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    // Single source of truth shared with the padding wrappers: the strip and
    // the 28px compensation can never disagree.
    const visible = useVersionBannerVisible(variant);
    const [switching, setSwitching] = useState(false);

    if (!visible) return null;

    // The banner in the legacy tree offers the new UI, and vice versa.
    const target: UiVersion = variant === "legacy" ? "new" : "legacy";

    const validToken = () => {
        const token = keycloak?.token;
        return token && typeof token === "string" && !keycloak.isTokenExpired() ? token : null;
    };

    const handleSwitch = async () => {
        setSwitching(true);
        const token = validToken();
        const destination = await mapPathAcrossVersions(pathname, target, {
            routeIdForProvider: async (providerId) => {
                if (!token) return null;
                const { results, ok } = await getRoutesByIntegration(token, "provider", providerId);
                // Ambiguous old links land on the first route of the provider
                // (the plan's default/first rule); a failed lookup is not "none".
                return ok && results.length ? results[0]?.id ?? null : null;
            },
            providerIdForRoute: async (routeId) => {
                if (!token) return null;
                try {
                    const route = await getRoute(token, routeId);
                    return route?.data_providers?.[0]?.id ?? null;
                } catch {
                    return null;
                }
            },
        });
        // Flip the flag first: the shell re-renders the other tree, and the
        // navigate below lands it on a URL in that tree's vocabulary.
        setUiVersion(target);
        navigate(destination, { replace: true });
        setSwitching(false);
    };

    // Slim full-width strip mounted inside the fixed nav wrapper, directly
    // under the environment stripe. Kept ≤28px tall: the app shell's
    // pt-[115px] leaves 29px of slack below the 86px nav, so the banner fits
    // without covering page content.
    return (
        <div
            data-testid="VersionBanner-div"
            className={`flex flex-row items-center justify-center gap-2 px-4 h-7 border-b border-gray-lines ${
                // Legacy tree: a light green wash makes the "you are on the old
                // version" strip register at a glance. The new tree's banner is
                // informational, so it stays flush with the nav.
                variant === "legacy" ? "bg-dark-green-50" : "bg-white"
            }`}
        >
            <span className="text-xs font-normal text-off-black">
                {variant === "legacy"
                    ? t("New version is here.")
                    : t("You're viewing the new version of Gundi.")}
            </span>
            {/* The way FORWARD is green (we want the click); the way back is
                quiet gray. */}
            <button
                type="button"
                data-testid="VersionBanner-button"
                disabled={switching}
                onClick={handleSwitch}
                className={`text-xs font-semibold underline underline-offset-2 disabled:opacity-50 ${variant === "legacy"
                    ? "text-dark-green hover:text-accent-green"
                    : "text-secondary-text hover:text-off-black"}`}
            >
                {variant === "legacy"
                    ? t("Switch to the new version")
                    : t("Switch back to the previous version")}
            </button>
        </div>
    );
}
